import { useCallback, useEffect, useMemo, useState } from "react";
import Alert from "@mui/material/Alert";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import TextField from "@mui/material/TextField";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import CloseOutlinedIcon from "@mui/icons-material/CloseOutlined";
import GroupAddOutlinedIcon from "@mui/icons-material/GroupAddOutlined";
import RefreshOutlinedIcon from "@mui/icons-material/RefreshOutlined";
import Page from "../Components/Page";
import { axiosInstance } from "../Components/axiosInstance.js";

function formatDate(value) {
  if (!value) return "Not available";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "Not available";
  return new Intl.DateTimeFormat("en-CA", {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(date);
}

function apiError(error, fallback) {
  const detail = error?.response?.data?.detail;
  if (typeof detail === "string") return detail;
  return error?.message || fallback;
}

function statusLabel(value) {
  return String(value || "UNKNOWN")
    .toLowerCase()
    .split("_")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
}

const STATUS_STYLES = {
  PENDING_APPROVAL: "border-amber-200 bg-amber-50 text-amber-900",
  OPTED_IN: "border-emerald-200 bg-emerald-50 text-emerald-800",
  REJECTED: "border-red-200 bg-red-50 text-red-800",
  REVOKED: "border-slate-200 bg-slate-100 text-slate-700",
};

function StatusTag({ value }) {
  const normalized = String(value || "UNKNOWN").toUpperCase();
  return (
    <span className={`rounded-[4px] border px-2 py-1 text-xs font-bold ${STATUS_STYLES[normalized] || STATUS_STYLES.REVOKED}`}>
      {statusLabel(normalized)}
    </span>
  );
}

function scopeLabels(enrollment) {
  const snapshot = enrollment?.consent_snapshot || {};
  return snapshot?.project?.data_scope || snapshot?.terms?.data_scope || [];
}

export default function AdminProjectEnrollmentsPage() {
  const [filter, setFilter] = useState("PENDING_APPROVAL");
  const [enrollments, setEnrollments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const [decision, setDecision] = useState(null);
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);

  document.title = "Project Approvals - Field To Market Canada";

  const loadEnrollments = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const response = await axiosInstance.get("/admin/project-enrollments", {
        params: filter === "ALL" ? { consent_status: "" } : { consent_status: filter },
      });
      const payload = response.data;
      setEnrollments(Array.isArray(payload) ? payload : payload?.items || []);
    } catch (loadError) {
      setEnrollments([]);
      setError(apiError(loadError, "Project enrollment requests could not be loaded."));
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => {
    loadEnrollments();
  }, [loadEnrollments]);

  const pendingCount = useMemo(
    () => enrollments.filter((item) => item.consent_status === "PENDING_APPROVAL").length,
    [enrollments]
  );

  function openDecision(enrollment, action) {
    setDecision({ enrollment, action });
    setNotes("");
    setMessage("");
  }

  async function submitDecision() {
    if (!decision) return;
    setSaving(true);
    setError("");
    try {
      const { enrollment, action } = decision;
      await axiosInstance.post(
        `/admin/project-enrollments/${enrollment.public_id}/${action}`,
        { notes: notes.trim() || null }
      );
      setDecision(null);
      setMessage(
        action === "approve"
          ? `${enrollment.grower_display_name || enrollment.grower_contact_email} is now enrolled in ${enrollment.project_name}.`
          : `The enrollment request for ${enrollment.project_name} was not approved.`
      );
      await loadEnrollments();
    } catch (decisionError) {
      setError(apiError(decisionError, "The enrollment decision could not be saved."));
    } finally {
      setSaving(false);
    }
  }

  return (
    <Page
      title="Project approvals"
      headerVariant="hybrid"
      contentWidth="standard"
      padding="px-4 py-6 md:px-6 md:py-8"
      introContent={<p>Review grower consent requests before project data becomes available to partners.</p>}
    >
      <div className="w-full space-y-5">
        <div className="flex flex-col gap-4 border-b border-slate-200 pb-5 sm:flex-row sm:items-center sm:justify-between">
          <div className="inline-flex w-fit rounded-[6px] border border-slate-300 bg-white p-1">
            {[
              { value: "PENDING_APPROVAL", label: "Pending" },
              { value: "ALL", label: "All decisions" },
            ].map((option) => (
              <button
                className={`rounded-[4px] px-4 py-2 text-sm font-bold ${filter === option.value ? "bg-[#243245] text-white" : "text-slate-600 hover:bg-slate-100"}`}
                key={option.value}
                onClick={() => setFilter(option.value)}
                type="button"
              >
                {option.label}
              </button>
            ))}
          </div>
          <Button startIcon={<RefreshOutlinedIcon />} variant="outlined" onClick={loadEnrollments}>
            Refresh
          </Button>
        </div>

        {message ? <Alert severity="success">{message}</Alert> : null}
        {error ? <Alert severity="error">{error}</Alert> : null}

        <section className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h2 className="text-xl font-black text-slate-950">
              {filter === "PENDING_APPROVAL" ? "Awaiting review" : "Enrollment decisions"}
            </h2>
            <p className="mt-1 text-sm text-slate-600">
              Approval activates partner access only to the project&apos;s consented data scope.
            </p>
          </div>
          <div className="rounded-[4px] border border-slate-200 bg-white px-3 py-2 text-sm font-bold text-slate-700">
            {filter === "PENDING_APPROVAL" ? pendingCount : enrollments.length} {filter === "PENDING_APPROVAL" ? "pending" : "records"}
          </div>
        </section>

        {loading ? (
          <div className="flex min-h-[280px] items-center justify-center"><CircularProgress color="success" /></div>
        ) : null}

        {!loading && !enrollments.length ? (
          <div className="border border-dashed border-slate-300 bg-white px-6 py-12 text-center">
            <GroupAddOutlinedIcon sx={{ color: "#64748b", fontSize: 34 }} />
            <h3 className="mt-3 text-lg font-black text-slate-900">No enrollment requests here</h3>
            <p className="mt-1 text-sm text-slate-500">
              New grower requests will appear here before any data is shared with a partner.
            </p>
          </div>
        ) : null}

        {!loading && enrollments.length ? (
          <div className="grid grid-cols-1 gap-4">
            {enrollments.map((enrollment) => {
              const scopes = scopeLabels(enrollment);
              const pending = enrollment.consent_status === "PENDING_APPROVAL";
              return (
                <article className="rounded-[8px] border border-slate-200 bg-white p-5 shadow-[0_8px_24px_rgba(18,28,45,0.04)]" key={enrollment.public_id}>
                  <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                    <div className="min-w-0">
                      <div className="flex flex-wrap items-center gap-2">
                        <h3 className="text-lg font-black text-slate-950">{enrollment.project_name}</h3>
                        <StatusTag value={enrollment.consent_status} />
                      </div>
                      <p className="mt-1 text-sm text-slate-500">{enrollment.partner_name || "Project partner"}</p>
                    </div>
                    {pending ? (
                      <div className="flex shrink-0 flex-wrap gap-2">
                        <Button color="error" startIcon={<CloseOutlinedIcon />} variant="outlined" onClick={() => openDecision(enrollment, "reject")}>Reject</Button>
                        <Button color="success" startIcon={<CheckCircleOutlineIcon />} variant="contained" onClick={() => openDecision(enrollment, "approve")}>Approve</Button>
                      </div>
                    ) : null}
                  </div>

                  <div className="mt-5 grid grid-cols-1 gap-4 border-t border-slate-200 pt-5 md:grid-cols-3">
                    <div>
                      <div className="text-xs font-bold uppercase text-slate-500">Grower</div>
                      <div className="mt-1 font-bold text-slate-900">{enrollment.grower_display_name || "Grower profile"}</div>
                      <div className="mt-0.5 break-all text-sm text-slate-500">{enrollment.grower_contact_email}</div>
                    </div>
                    <div>
                      <div className="text-xs font-bold uppercase text-slate-500">Requested</div>
                      <div className="mt-1 font-bold text-slate-900">{formatDate(enrollment.requested_at || enrollment.created_at)}</div>
                      {enrollment.reviewed_at ? <div className="mt-0.5 text-sm text-slate-500">Reviewed {formatDate(enrollment.reviewed_at)}</div> : null}
                    </div>
                    <div>
                      <div className="text-xs font-bold uppercase text-slate-500">Consented data scope</div>
                      <div className="mt-2 flex flex-wrap gap-1.5">
                        {scopes.length ? scopes.map((scope) => <span className="rounded-[4px] border border-slate-200 bg-slate-50 px-2 py-1 text-xs font-semibold text-slate-700" key={scope}>{scope.replaceAll("_", " ")}</span>) : <span className="text-sm text-slate-500">No data scope configured</span>}
                      </div>
                    </div>
                  </div>
                  {enrollment.review_notes ? <p className="mt-4 border-l-2 border-slate-300 pl-3 text-sm text-slate-600">Administrator note: {enrollment.review_notes}</p> : null}
                </article>
              );
            })}
          </div>
        ) : null}
      </div>

      <Dialog fullWidth maxWidth="sm" open={Boolean(decision)} onClose={() => !saving && setDecision(null)}>
        <DialogTitle>{decision?.action === "approve" ? "Approve enrollment?" : "Reject enrollment request?"}</DialogTitle>
        <DialogContent>
          <p className="mb-4 text-sm leading-6 text-slate-600">
            {decision?.action === "approve"
              ? "The grower will be enrolled and the partner can access the consented project data scope. The grower will receive an email confirmation."
              : "The grower will not be enrolled or exposed to the partner. Add a useful explanation when appropriate."}
          </p>
          <TextField
            fullWidth
            label={decision?.action === "approve" ? "Administrative note (optional)" : "Reason (optional)"}
            multiline
            minRows={3}
            onChange={(event) => setNotes(event.target.value)}
            value={notes}
          />
        </DialogContent>
        <DialogActions sx={{ padding: "16px 24px" }}>
          <Button disabled={saving} onClick={() => setDecision(null)}>Cancel</Button>
          <Button
            color={decision?.action === "approve" ? "success" : "error"}
            disabled={saving}
            startIcon={saving ? <CircularProgress color="inherit" size={16} /> : decision?.action === "approve" ? <CheckCircleOutlineIcon /> : <CloseOutlinedIcon />}
            variant="contained"
            onClick={submitDecision}
          >
            {saving ? "Saving..." : decision?.action === "approve" ? "Approve enrollment" : "Reject request"}
          </Button>
        </DialogActions>
      </Dialog>
    </Page>
  );
}
